"use client";

import React, { useRef, useState } from "react";

import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import Picture from "@/components/ui-kit/Picture/Picture";
import type {
  ScienceBackgroundImage,
  ScienceResponsiveImage,
} from "@/data/hair/brand/learn/science/science.types";
import { cn } from "@/utils/general";

export const NAVIGATION_HEIGHT = 52;
export const HEADER_HEIGHT_MOBILE = 96;
export const HEADER_HEIGHT = NAVIGATION_HEIGHT + HEADER_HEIGHT_MOBILE;
export const expandTravelDistance = 200;

const defaultAnimationConfig = {
  imageScale: true,
  clipPath: true,
  backgroundColor: true,
  blur: false,
  overlay: false,
};

type AnimationConfig = Partial<typeof defaultAnimationConfig>;

type ScrollRevealProps = {
  selector: string;
  background?: React.ReactNode;
  backgroundSequence?: ScienceBackgroundImage[];
  backgroundScaleInitial?: number;
  backgroundScale?: number;
  backgroundColor?: string;
  content: (args: { intro: boolean }) => React.ReactNode;
  offset?: number;
  showContentTreshold?: number;
  pinDistance?: number | null;
  animationConfig?: AnimationConfig;
  className?: string;
};

function ResponsiveImage({
  src,
  alt,
  className,
  loading = "lazy",
}: {
  src?: ScienceResponsiveImage;
  alt: string;
  className?: string;
  loading?: "lazy" | "eager";
}) {
  if (!src?.mobile && !src?.desktop) return null;
  const fallback = src.mobile || src.desktop || "";
  return (
    <div className="h-full">
      <Picture
        src={fallback}
        sources={
          src.mobile && src.desktop ? [{ media: "(min-width: 768px)", srcSet: src.desktop }] : []
        }
        alt={alt}
        loading={loading}
        className={className}
      />
    </div>
  );
}

function BackgroundSequenceImage({
  src,
  alt,
  settings,
  index,
  start,
  end,
}: ScienceBackgroundImage & {
  settings: Record<string, unknown>;
  index: number;
  start: number;
  end: number;
}) {
  const imageRef = useRef<HTMLDivElement>(null);

  useGSAP(() => {
    if (index === 0 || !imageRef.current) return;

    gsap.fromTo(
      imageRef.current,
      { opacity: 0 },
      {
        opacity: 1,
        scrollTrigger: {
          ...settings,
          start: `-=${start - index * 50}`,
          end: `+=${end - index * 50}`,
        },
      }
    );
  });

  return (
    <div ref={imageRef}>
      <ResponsiveImage
        src={src}
        alt={alt}
        loading="eager"
        className="absolute top-0 w-full object-contain bg-[#40482B]"
      />
    </div>
  );
}

function BackgroundSequence({
  images,
  settings,
  start,
  end,
}: {
  images: ScienceBackgroundImage[];
  settings: Record<string, unknown>;
  start: number;
  end: number;
}) {
  return (
    <div className="absolute top-0 w-full h-full bg-black overflow-hidden origin-top will-change-[filter]">
      {images.map((image, index) => (
        <BackgroundSequenceImage
          key={index}
          index={index}
          {...image}
          settings={settings}
          start={start}
          end={end}
        />
      ))}
    </div>
  );
}

export function ScrollRevealContent({
  children,
  className,
  intro,
  style,
}: {
  children: React.ReactNode;
  className?: string;
  intro?: boolean;
  style?: React.CSSProperties;
}) {
  return (
    <div
      data-intro={intro}
      style={style}
      className={cn(
        "flex-1 [&_[data-intro-element]]:opacity-100",
        intro && "[&>*]:opacity-0 [&>[data-intro-element]]:opacity-100",
        "[&>*]:transition-opacity [&>*]:duration-400",
        className
      )}
    >
      {children}
    </div>
  );
}

export default function ScienceScrollReveal({
  selector,
  background,
  backgroundSequence,
  backgroundScaleInitial = 1,
  backgroundScale = 1.6,
  backgroundColor = "#000000",
  content,
  offset = 0,
  showContentTreshold = 0.95,
  pinDistance,
  animationConfig,
  className,
  ...rest
}: ScrollRevealProps) {
  const backgroundRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const [showChildren, setShowChildren] = useState(false);

  const scrollTriggerSettings = {
    start: `-=${HEADER_HEIGHT + expandTravelDistance + offset}`,
    end: `+=${expandTravelDistance}`,
    scrub: 2,
    trigger: `[data-scroll-reveal='${selector}']`,
    anticipatePin: 1,
  };

  const computedAnimationConfig = {
    ...defaultAnimationConfig,
    ...animationConfig,
  };

  useGSAP(() => {
    if (!computedAnimationConfig.imageScale || !backgroundRef.current) return;
    gsap.fromTo(
      backgroundRef.current,
      { scale: backgroundScaleInitial },
      { scale: backgroundScale, scrollTrigger: scrollTriggerSettings }
    );
  });

  useGSAP(() => {
    if (!computedAnimationConfig.blur || !backgroundRef.current) return;
    gsap.fromTo(
      backgroundRef.current,
      { filter: "blur(0)" },
      {
        filter: "blur(25px)",
        opacity: 0.6,
        scrollTrigger: scrollTriggerSettings,
      }
    );
  });

  useGSAP(() => {
    if (!computedAnimationConfig.backgroundColor || !containerRef.current) return;
    gsap.fromTo(
      containerRef.current,
      { background: "transparent" },
      { background: backgroundColor, scrollTrigger: scrollTriggerSettings }
    );
  });

  useGSAP(() => {
    if (!computedAnimationConfig.overlay || !overlayRef.current) return;
    gsap.fromTo(
      overlayRef.current,
      { opacity: 0 },
      { opacity: 0.5, scrollTrigger: scrollTriggerSettings }
    );
  });

  useGSAP(() => {
    if (!containerRef.current) return;
    gsap.fromTo(
      containerRef.current,
      {},
      {
        scrollTrigger: {
          ...scrollTriggerSettings,
          onUpdate: (self) => {
            setShowChildren(self.progress >= showContentTreshold);
          },
        },
      }
    );
  });

  useGSAP(() => {
    if (pinDistance && pinDistance > 0 && containerRef.current) {
      ScrollTrigger.create({
        start: `-=${HEADER_HEIGHT}`,
        end: `+=${pinDistance}`,
        pin: true,
        trigger: containerRef.current,
        scrub: 2,
        anticipatePin: 1,
      });
    }
  });

  return (
    <div
      ref={containerRef}
      data-scroll-reveal={selector}
      className={cn("relative w-full overflow-hidden scroll-reveal", className)}
      {...rest}
    >
      <div
        ref={backgroundRef}
        className="absolute top-0 w-full h-full bg-black overflow-hidden origin-top will-change-[filter] md:origin-center"
      >
        {backgroundSequence ? (
          <BackgroundSequence
            settings={scrollTriggerSettings}
            start={HEADER_HEIGHT + expandTravelDistance + offset}
            end={expandTravelDistance}
            images={backgroundSequence}
          />
        ) : (
          background
        )}
      </div>

      {computedAnimationConfig.overlay && (
        <div
          ref={overlayRef}
          className="absolute bottom-0 left-0 w-full h-full opacity-0"
          style={{ backgroundColor }}
        />
      )}

      {Boolean(content) && (
        <div
          data-intro-animation={showChildren}
          className={cn(
            // Gatsby ContentWrapper: flex column, pb 60px, min-height calc until `small` (768)
            "scroll-reveal-content-wrapper flex flex-col pb-[60px]",
            "min-h-[calc(100vh-var(--science-scroll-reveal-chrome))]",
            "md:min-h-0 md:h-full md:pb-0"
          )}
          style={
            {
              "--science-scroll-reveal-chrome": `${HEADER_HEIGHT + offset}px`,
            } as React.CSSProperties
          }
        >
          {content({ intro: !showChildren })}
        </div>
      )}
    </div>
  );
}
